import { BrokerGroupSelect } from '@/components/selects/BrokerGroupSelect';
import { useGetBrokerGroupsQuery, useGetUserDetailsQuery } from '@/features/user/userApi';
import { useFormikContext } from 'formik';
import React from 'react';
import { useSelector } from 'react-redux';
import FieldFormControl from '../FieldFormControl';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { InsightSelectFormikWrapper } from '../InsightSelectFormikWrapper';
import { FIELD_TYPES } from '@/features/insightFields';
import InsightFieldSelect from '@/components/selects/InsightFieldSelect';
import { useClientSchemaContext } from '@/context/clientSchemaContext';
import { VIEW_TYPES } from '@/features/config/helper';

const BrokerageCreationInfoField = ({ field, type, disabled }) => {
	const { values, setFieldValue, getFieldMeta, getFieldProps, getFieldHelpers } =
		useFormikContext();
	const { view } = useClientSchemaContext();

	const endpoint = field.field?.endpoint;
	const name = field.name;
	const fieldType = name?.endsWith('Id') ? name.slice(0, -2) : name;

	const isInsightSelectField = FIELD_TYPES.includes(fieldType);

	const { onBlur: onFieldBlur } = getFieldProps(name);
	const { setTouched } = getFieldHelpers(name);

	const onBlur = (e) => {
		onFieldBlur(e);
		setTouched(true, true);
	};

	const { error: errorMessage, touched } = getFieldMeta(name);
	const error = touched && Boolean(errorMessage);

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	// Permission Group / brokerGroupId are selectable on create, disabled on edit.
	// User needs to change salesTeam owner/user if they want to update...
	const fieldDisabled =
		['RecordPermissionsGroupId', 'brokerGroupId'].includes(name) && view === VIEW_TYPES.update;

	const userQuery = useGetUserDetailsQuery({ email }, { skip: !email });

	const brokerGroupIdsArray = userQuery.data?.brokerGroupArray ?? [];

	const brokerGroupsQuery = useGetBrokerGroupsQuery(
		{ ids: brokerGroupIdsArray, showAdmin: false },
		{
			skip: !brokerGroupIdsArray?.length,
		},
	);

	const { getFeatureFlags } = useFeatureFlagContext();
	const clientConfigInsightSelection = Boolean(
		getFeatureFlags('clientConfigInsightSelection')?.active,
	);

	const handleChange = (newId) => {
		const newGroup = (brokerGroupsQuery.data ?? []).find((group) => group.id === newId);
		setFieldValue(field.name, newGroup?.[type]);
		setFieldValue(`${field.name}-docId`, newGroup?.id);
		setFieldValue(`${field.name}-obj`, newGroup);
		setFieldValue(`${field.name}-type`, 'brokerGroup');
	};

	if (clientConfigInsightSelection && endpoint) {
		return <InsightSelectFormikWrapper {...{ disabled, field }} />;
	}

	if (isInsightSelectField) {
		return (
			<FieldFormControl {...{ disabled, field }}>
				<InsightFieldSelect
					value={values[name]}
					fieldType={fieldType}
					onChange={(newValue) => setFieldValue(name, newValue?.insightId)}
					{...{ onBlur, error, disabled }}
				/>
			</FieldFormControl>
		);
	}

	return (
		<FieldFormControl {...{ disabled, field }}>
			<BrokerGroupSelect
				brokerGroupId={values[`${name}-docId`]}
				onChange={handleChange}
				isGroupDisabled={(group) => group?.[type] == null}
				sx={{
					'& .MuiInputBase-root': {
						cursor: 'pointer',
						py: '9px',
					},
				}}
				{...{ onBlur, error, disabled: disabled || fieldDisabled }}
			/>
		</FieldFormControl>
	);
};

export default BrokerageCreationInfoField;
